import { inject, Injectable, Injector } from '@angular/core';
import { outputToObservable } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { AbilityService } from 'ability';
import { cloneDeep, isNil } from 'lodash';
import { SidepanelCrudBase, SidepanelService } from 'mbs-ui-kit';
import { SidePanelBackupService } from 'mbs-ui-shared@components/sidepanel-backup/sidepanel-backup.service';
import { SidepanelConnectSettingsComponent } from 'mbs-ui-shared@components/sidepanel-connect-settings/sidepanel-connect-settings.component';
import { RoutingPath } from 'mbs-ui-shared@constants';
import { AuthFacade } from 'mbs-ui-shared@facades/auth.facade';
import { ComputersFacade } from 'mbs-ui-shared@facades/computers.facade';
import { LicensesFacade } from 'mbs-ui-shared@facades/licenses.facade';
import { Administrator, LicenseType, PermissionsEnum } from 'mbs-ui-shared@models';
import Computer, { ComputerModeAdapter, ComputerModeAdapterByPanel, ComputerSidePanelUriParams } from 'mbs-ui-shared@models/Computer';
import { BackupSidePanelTab } from 'mbs-ui-shared@models/backup/sidepanel-tab';
import { enabledRMMSidePanelTabs, RMMSidePanelTab } from 'mbs-ui-shared@models/rmm/SidepanelTab';
import { SidepanelId, SidepanelRouteType } from 'mbs-ui-shared@models/sidepanel';
import { Observable, of } from 'rxjs';
import { catchError, filter, finalize, map, switchMap, take } from 'rxjs/operators';

@UntilDestroy()
@Injectable({
  providedIn: 'root'
})
export class SharedComputerSidepanelWrapper {
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private sidepanelService = inject(SidepanelService);
  private ability = inject(AbilityService);
  private computersFacade = inject(ComputersFacade);
  private licensesFacade = inject(LicensesFacade);
  private auth = inject(AuthFacade);
  private injector = inject(Injector);
  private sidepanelBackupService = inject(SidePanelBackupService);

  currentUser: Administrator;
  private licenseCheckStarted = false;

  constructor() {
    this.auth.currentUser$.subscribe((user) => (this.currentUser = user));
  }

  getComputerByHid(hid: string): Observable<Computer> {
    this.computersFacade.loadComputerByHid({ hid });

    return this.computersFacade.getByHid$(hid, true).pipe(take(1), untilDestroyed(this));
  }

  openSidepanelByComputerHid(sidepanel: SidepanelRouteType, hid: string, tab?: string): void {
    this.computersFacade
      .getByHid$(hid)
      .pipe(filter(Boolean), take(1), untilDestroyed(this))
      .subscribe((computer) => this.openSidepanelByComputer(sidepanel, computer, tab));
    this.computersFacade.loadComputerByHid({ hid });
  }

  openSidepanelByComputer(sidepanel: SidepanelRouteType, computer: Computer, tab?: string): void {
    switch (sidepanel) {
      case SidepanelRouteType.RMM:
        this.handleOpenRmmInfo(computer, tab as RMMSidePanelTab);
        break;
      case SidepanelRouteType.Backup:
        this.handleOpenBackupPanel(computer, tab as BackupSidePanelTab);
        break;
      case SidepanelRouteType.ClientSettings:
        this.handleOpenBackupPanel(computer, BackupSidePanelTab.agentSettingsGeneral);
        break;
      case SidepanelRouteType.ConnectSettings:
        this.handleOpenConnectSettings(computer);
        break;
      default:
        this.updateSlug({ id: null });
        break;
    }
  }

  handleOpenRmmInfo(computer: Computer, activeTab: RMMSidePanelTab = RMMSidePanelTab.general): void {
    if (this.ability.cannot('read', PermissionsEnum.Rmm)) {
      return;
    }

    this.checkRmmPermission(computer).subscribe((hasPermission) =>
      hasPermission ? this.openRmmSidepanel(computer, activeTab) : this.updateSlug({ id: null })
    );
  }

  private openRmmSidepanel(computer: Computer, activeTab: RMMSidePanelTab = RMMSidePanelTab.general): void {
    import('mbs-ui-shared@components/sidepanel-rmm/sidepanel-rmm.component').then((module) => {
      const SidepanelRmmComponent = module.SidepanelRmmComponent;

      // switch current computer in opened sidepanel
      if (this.sidepanelService.isOpened(SidepanelRmmComponent)) {
        const panel = this.sidepanelService.get(SidepanelRmmComponent);
        return this.setRMMPanelData<typeof panel>(panel, activeTab, computer);
      }

      // open new sidepanel
      this.sidepanelService.closeAll();
      const rmmPanel = this.sidepanelService.add(SidepanelRmmComponent, null, this.injector);

      rmmPanel.switchPanel.pipe(take(1), untilDestroyed(this)).subscribe((data) => this.switchPanel(data.panel, data.computer));
      rmmPanel.tabChanged.pipe(untilDestroyed(this)).subscribe((tab) => this.updateSlug({ tab }));
      rmmPanel.close.pipe(take(1), untilDestroyed(this)).subscribe(() => this.updateSlug({ id: null, sidepanel: SidepanelRouteType.RMM }));

      this.sidepanelService
        .openByType(SidepanelRmmComponent)
        .pipe(take(1), untilDestroyed(this))
        .subscribe(() => this.setRMMPanelData<typeof rmmPanel>(rmmPanel, activeTab, computer));
    });
  }

  private setRMMPanelData<T extends SidepanelCrudBase<Computer>>(panel: T, activeTab: RMMSidePanelTab, computer: Computer): void {
    const defaultTab = RMMSidePanelTab.general;
    let tab = activeTab;
    const enabledTabs = enabledRMMSidePanelTabs(computer, this.ability)
      .filter((tab) => tab.enabled)
      .map((tab) => tab.tab);
    const firstEnabledTab = enabledTabs.length ? enabledTabs[0] : null;
    const allowedDefaultTab = enabledTabs.includes(defaultTab) ? defaultTab : firstEnabledTab;

    // be carefull to select correct active tab before initialisation!
    if (!enabledTabs.includes(tab)) {
      tab = null;
    }

    tab = tab && panel.tabset.items.toArray().some((panelTab) => panelTab.id === tab) ? tab : allowedDefaultTab;

    panel.data$.next(computer);
    panel.tabset.select(tab);
    this.updateSlug({ tab });
  }

  handleOpenBackupPanel(computer: Computer, activeTab?: BackupSidePanelTab): void {
    if (this.ability.cannot('read', PermissionsEnum.Backup)) {
      return;
    }

    import('mbs-ui-shared@components/sidepanel-backup/sidepanel-backup.module').then(() => {
      import('mbs-ui-shared@components/sidepanel-backup/sidepanel-backup.component').then((module) => {
        const SidepanelBackupComponent = module.SidepanelBackupComponent;

        // switch current computer in opened sidepanel
        if (this.sidepanelService.isOpened(SidepanelBackupComponent)) {
          const panel = this.sidepanelService.get(SidepanelBackupComponent);
          return this.setBackupPanelData<typeof panel>(panel, activeTab, computer);
        }

        // open new sidepanel
        this.sidepanelService.closeAll();
        const backupPanel = this.sidepanelService.add(SidepanelBackupComponent, null, this.injector);

        outputToObservable(backupPanel.switchPanel)
          .pipe(take(1), untilDestroyed(this))
          .subscribe((data) => this.switchPanel(data.panel, data.computer));
        outputToObservable(backupPanel.tabChanged)
          .pipe(untilDestroyed(this))
          .subscribe((tab) => this.updateSlug({ tab }));
        backupPanel.close
          .pipe(take(1), untilDestroyed(this))
          .subscribe(() => this.updateSlug({ id: null, sidepanel: SidepanelRouteType.Backup }));

        this.sidepanelService
          .openByType(SidepanelBackupComponent)
          .pipe(take(1), untilDestroyed(this))
          .subscribe(() => this.setBackupPanelData<typeof backupPanel>(backupPanel, activeTab ?? BackupSidePanelTab.backupPlans, computer));
      });
    });
  }

  private setBackupPanelData<T extends SidepanelCrudBase<Computer>>(
    backupPanel: T,
    activeTab: BackupSidePanelTab,
    computer: Computer
  ): void {
    backupPanel.data$.next(computer);
    // The tabs that depend on the computer appear only on the next render, and a tab the panel has not rendered yet
    // counts as missing, so after a reload the URL tab would lose to the default one.
    setTimeout(() => this.selectBackupPanelTab<T>(backupPanel, activeTab, computer));
  }

  private selectBackupPanelTab<T extends SidepanelCrudBase<Computer>>(backupPanel: T, tab: BackupSidePanelTab, computer: Computer): void {
    const defaultTab = Computer.isDRServer(computer) ? BackupSidePanelTab.virtualMachines : BackupSidePanelTab.general;
    let activeTab = tab;

    if (this.sidepanelBackupService.isTabDisabled(tab, computer)) {
      activeTab = null;
    }

    !activeTab && (activeTab = BackupSidePanelTab.backupStorage);
    const activeTabExist = activeTab && backupPanel.tabset.items.toArray().some((tab) => tab.id === activeTab);

    // be carefull to select correct active tab before initialisation!
    const selectedTab = activeTabExist ? activeTab : defaultTab;

    backupPanel.tabset.select(selectedTab);
    this.updateSlug({ tab: selectedTab });
  }

  handleOpenConnectSettings(computer: Computer) {
    // switch current computer in opened sidepanel
    if (this.sidepanelService.isOpened(SidepanelConnectSettingsComponent)) {
      const panel = this.sidepanelService.get(SidepanelConnectSettingsComponent);
      return panel.data$.next(computer);
    }

    // open new sidepanel
    this.sidepanelService.closeAll();
    const panel = this.sidepanelService.add(SidepanelConnectSettingsComponent, null, this.injector);

    panel.close
      .pipe(take(1), untilDestroyed(this))
      .subscribe(() => this.updateSlug({ id: null, sidepanel: SidepanelRouteType.ConnectSettings }));

    this.sidepanelService
      .openByType(SidepanelConnectSettingsComponent)
      .pipe(take(1), untilDestroyed(this))
      .subscribe(() => panel.data$.next(computer));
  }

  updateSlug(params: ComputerSidePanelUriParams): void {
    if (!this.isPageSupported()) return;

    const innerParams = cloneDeep(params);
    const currentParams = {
      id: this.route.snapshot.params['id'],
      sidepanel: this.route.snapshot.queryParams['sidepanel'],
      tab: this.route.snapshot.queryParams['tab']
    } as ComputerSidePanelUriParams;

    const queryParams: ComputerSidePanelUriParams = {};
    const commands: string[] = [];

    if (innerParams?.id || innerParams?.id === null) {
      if (!innerParams.id && innerParams.sidepanel && innerParams.sidepanel !== currentParams.sidepanel) {
        return;
      }

      const basePath = this.isComputersPageActive() ? `${this.router.url.split('Computers')[0]}Computers` : RoutingPath.ApNewBackupHistory;

      commands.push(basePath);
      innerParams?.id && commands.push(innerParams.id);

      if (!innerParams?.id) {
        queryParams.sidepanel = null;
        queryParams.tab = null;

        innerParams?.sidepanel && delete innerParams.sidepanel;
        innerParams?.tab && delete innerParams.tab;
      }

      delete innerParams.id;
    }

    if (innerParams?.tab && !currentParams.sidepanel) {
      delete innerParams.tab;

      innerParams?.tab && delete innerParams.tab;
    }

    Object.keys(innerParams).forEach((key) => {
      queryParams[key] = !isNil(innerParams[key]) ? innerParams[key] : null;
    });

    void this.router.navigate(commands, {
      relativeTo: this.route,
      queryParamsHandling: 'merge',
      queryParams
    });
  }

  private switchPanel(panel: SidepanelRouteType, computer: Computer): void {
    if (!this.isPageSupported()) {
      switch (panel) {
        case SidepanelRouteType.RMM:
          this.handleOpenRmmInfo(computer);
          break;
        case SidepanelRouteType.Backup:
          this.handleOpenBackupPanel(computer);
          break;
        case SidepanelRouteType.ClientSettings:
          this.handleOpenBackupPanel(computer, BackupSidePanelTab.agentSettingsGeneral);
          break;
      }
      return;
    }
    const mode = ComputerModeAdapterByPanel.find((modeUrl) => panel === modeUrl.panel).mode;
    const url = ComputerModeAdapter.find((modeUrl) => mode === modeUrl.mode);

    if (!url) return;

    this.router.navigate([url.url, computer.hid], { queryParams: { sidepanel: panel } });
  }

  isPageSupported(): boolean {
    return this.router.url.includes(RoutingPath.ApNewBackupHistory) || this.isComputersPageActive();
  }

  isComputersPageActive(): boolean {
    return ComputerModeAdapter.some((modeUrl) => this.router.url.includes(modeUrl.url));
  }

  checkRmmPermission(computer?: Computer): Observable<boolean> {
    if (this.licenseCheckStarted) return of(false);

    if (this.sidepanelService.isOpenedById(SidepanelId.RMM)) return of(true);

    if (this.ability.can('read', PermissionsEnum.RmmPerEndpoint)) return of(true);

    if (this.ability.can('read', PermissionsEnum.RmmPerAdminLicenseExists) && this.ability.can('read', PermissionsEnum.RmmCeEnabled)) {
      if (computer && !computer?.includedInRmmCe) {
        this.licenseCheckStarted = true;

        return this.licensesFacade.openRMMCELimitModal().pipe(
          untilDestroyed(this),
          finalize(() => (this.licenseCheckStarted = false))
        );
      } else {
        return of(true);
      }
    }

    this.licenseCheckStarted = true;

    return of(this.ability.can('read', PermissionsEnum.RmmPerAdminLicenseExists)).pipe(
      switchMap((canRead) =>
        canRead || this.currentUser.IsSuperAdmin
          ? of(true)
          : this.licensesFacade
              .openGrantLicenseDialogueWithInfoModal({
                email: this.currentUser.Email,
                adminID: this.currentUser.Id,
                filter: [LicenseType.RMM]
              })
              .pipe(
                map((licenseAssign) => !!licenseAssign?.license && !this.licensesFacade.isLicenseExpired(licenseAssign.license)),
                catchError(() => of(false))
              )
      ),
      take(1),
      map(Boolean),
      untilDestroyed(this),
      finalize(() => (this.licenseCheckStarted = false))
    );
  }

  closeAllSidepanels(): void {
    this.sidepanelService
      .closeAll()
      .pipe(take(1))
      .subscribe(() => this.updateSlug({ id: null }));
  }
}
